import React, { createContext, useContext, useMemo, useState } from 'react';
import { MainLayoutProps } from '../../interfaces/screens/mainLayout';
import { useDeepCompareEffect } from '../../utils/hooks/useDeepCompareEffect';

/**
 * Interface defining the state and methods for the MainLayout context
 * @interface MainLayoutState
 */
export interface MainLayoutState {
  /** Current properties for the main layout */
  props: MainLayoutProps;
  /** Function to update main layout properties */
  setProps: React.Dispatch<React.SetStateAction<MainLayoutProps>>;
}

/**
 * Context for managing main layout state
 */
const MainLayoutContext = createContext<MainLayoutState>(undefined);

/**
 * Hook to access the MainLayout context
 * @returns {MainLayoutState} The main layout context state
 * @throws {Error} If used outside of MainLayoutProvider
 */
export const useMainLayout = () => {
  const context = useContext(MainLayoutContext);
  if (!context) {
    throw new Error('useMainLayout must be used within a HeaderProvider');
  }
  return context;
};

/**
 * Provider component for the MainLayout context
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const MainLayoutProvider: React.FC<any> = ({ children }) => {
  const [props, setProps] = useState<MainLayoutProps>({});

  return <MainLayoutContext.Provider value={{ props, setProps }}>{children}</MainLayoutContext.Provider>;
};

/**
 * Custom hook to set the Main Layout Props
 * @param {MainLayoutProps} screenProps - Properties to set for the main layout
 */
export const useSetMainLayoutProps = (screenProps?: MainLayoutProps) => {
  const { setProps } = useMainLayout();
  const memoizedScreenProps = useMemo(() => screenProps, [screenProps]);

  // Use the custom deep comparison hook
  useDeepCompareEffect(() => {
    if (memoizedScreenProps !== null) {
      setProps(memoizedScreenProps || {}); // Set new screenProps only if changed
    }
  }, [memoizedScreenProps]);
};
